import { ProductFeature, SeatType } from '@/api/generated/api.enum';
import type { Analytics, AnalyticsEventPayload, AnalyticsUserProperties } from '@/services/analytics';
import { AnalyticsEvents, type AnalyticsEventName } from '@/services/analytics/events';
import {
  computeProductPriceBreakdownCents,
  DEFAULT_CURRENCY,
  getTrialUpsellDiscountPercent,
  type BillingInterval,
  type ProductWithMeta,
} from '@/utils/billing';
import { nowMs } from 'common/utils/date.utils';

export const CHECKOUT_OFFER_SOURCE_QUERY_PARAM = 'checkout_offer_source';

export const CheckoutOfferSource = {
  OnboardingPlanSelection: 'onboarding_plan_selection',
  OnboardingExpressCheckout: 'onboarding_express_checkout',
  TrialUpgradeButton: 'trial_upgrade_btn',
  BasisUpgradeButton: 'basis_upgrade_btn',
  TrialAdminPlans: 'trial_admin_plans',
  BasisAdminPlans: 'basis_admin_plans',
  TrialIntermittent: 'trial_intermittent',
  BasisIntermittent: 'basis_intermittent',
  TrialDiscountButton: 'trial_discount_btn',
  TrialFeedbackDiscount: 'trial_feedback_discount',
  BasisFeatureLockStatistic: 'basis_feature_lock_statistic',
  BasisFeatureLockReport: 'basis_feature_lock_report',
  BasisFeatureLockCollaboration: 'basis_feature_lock_collaboration',
  BasisFeatureLockTenantCustomization: 'basis_feature_lock_tenant_customization',
  BasisFeatureLockGradeCustomization: 'basis_feature_lock_grade_customization',
  BasisFeatureLockAccessRights: 'basis_feature_lock_access_rights',
  BasisFeatureLockSupportMeasures: 'basis_feature_lock_support_measures',
  BasisFeatureLockUnknown: 'basis_feature_lock_unknown',
} as const;

export type CheckoutOfferSource = (typeof CheckoutOfferSource)[keyof typeof CheckoutOfferSource];

type CheckoutOfferEvents = {
  buy: readonly AnalyticsEventName[];
  lockedFeature?: string;
  summary: AnalyticsEventName;
  show: readonly AnalyticsEventName[];
};

const CHECKOUT_OFFER_EVENTS: Record<CheckoutOfferSource, CheckoutOfferEvents> = {
  [CheckoutOfferSource.OnboardingPlanSelection]: {
    buy: [AnalyticsEvents.checkoutOffer.onboardingPlanSelectionT2p],
    show: [AnalyticsEvents.checkoutOffer.onboardingPlanSelectionImp],
    summary: AnalyticsEvents.funnelSummary.t2p,
  },
  [CheckoutOfferSource.OnboardingExpressCheckout]: {
    buy: [AnalyticsEvents.checkoutOffer.onboardingExpressCheckoutT2p],
    show: [AnalyticsEvents.checkoutOffer.onboardingExpressCheckoutImp],
    summary: AnalyticsEvents.funnelSummary.t2p,
  },
  [CheckoutOfferSource.TrialUpgradeButton]: {
    buy: [AnalyticsEvents.checkoutOffer.trialUpgradeButtonT2p],
    show: [AnalyticsEvents.checkoutOffer.trialUpgradeButtonImp],
    summary: AnalyticsEvents.funnelSummary.t2p,
  },
  [CheckoutOfferSource.BasisUpgradeButton]: {
    buy: [AnalyticsEvents.checkoutOffer.basisUpgradeButtonF2p],
    show: [AnalyticsEvents.checkoutOffer.basisUpgradeButtonImp],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.TrialAdminPlans]: {
    buy: [AnalyticsEvents.checkoutOffer.trialAdminPlansT2p],
    show: [AnalyticsEvents.checkoutOffer.trialAdminPlansImp],
    summary: AnalyticsEvents.funnelSummary.t2p,
  },
  [CheckoutOfferSource.BasisAdminPlans]: {
    buy: [AnalyticsEvents.checkoutOffer.basisAdminPlansF2p],
    show: [AnalyticsEvents.checkoutOffer.basisAdminPlansImp],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.TrialIntermittent]: {
    buy: [AnalyticsEvents.checkoutOffer.trialIntermittentT2p],
    show: [AnalyticsEvents.checkoutOffer.trialIntermittentImp],
    summary: AnalyticsEvents.funnelSummary.t2p,
  },
  [CheckoutOfferSource.BasisIntermittent]: {
    buy: [AnalyticsEvents.checkoutOffer.basisIntermittentF2p],
    show: [AnalyticsEvents.checkoutOffer.basisIntermittentImp],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.TrialDiscountButton]: {
    buy: [AnalyticsEvents.checkoutOffer.trialDiscountButtonT2p],
    show: [AnalyticsEvents.checkoutOffer.trialDiscountButtonImp],
    summary: AnalyticsEvents.funnelSummary.t2p,
  },
  // The feedback-dialog impression is tracked when feedback is opened, not when discounted pricing is displayed.
  [CheckoutOfferSource.TrialFeedbackDiscount]: {
    buy: [AnalyticsEvents.checkoutOffer.trialFeedbackT2p],
    show: [],
    summary: AnalyticsEvents.funnelSummary.t2p,
  },
  [CheckoutOfferSource.BasisFeatureLockStatistic]: {
    buy: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockStatisticF2p,
      AnalyticsEvents.checkoutOffer.basisFeatureLockF2p,
    ],
    lockedFeature: ProductFeature.statistic,
    show: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockStatisticImp,
      AnalyticsEvents.checkoutOffer.basisFeatureLockImp,
    ],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.BasisFeatureLockReport]: {
    buy: [AnalyticsEvents.checkoutOffer.basisFeatureLockReportF2p, AnalyticsEvents.checkoutOffer.basisFeatureLockF2p],
    lockedFeature: ProductFeature.report,
    show: [AnalyticsEvents.checkoutOffer.basisFeatureLockReportImp, AnalyticsEvents.checkoutOffer.basisFeatureLockImp],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.BasisFeatureLockCollaboration]: {
    buy: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockCollaborationF2p,
      AnalyticsEvents.checkoutOffer.basisFeatureLockF2p,
    ],
    lockedFeature: ProductFeature.collaboration,
    show: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockCollaborationImp,
      AnalyticsEvents.checkoutOffer.basisFeatureLockImp,
    ],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.BasisFeatureLockTenantCustomization]: {
    buy: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockTenantCustomizationF2p,
      AnalyticsEvents.checkoutOffer.basisFeatureLockF2p,
    ],
    lockedFeature: ProductFeature.tenantCustomization,
    show: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockTenantCustomizationImp,
      AnalyticsEvents.checkoutOffer.basisFeatureLockImp,
    ],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.BasisFeatureLockGradeCustomization]: {
    buy: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockGradeCustomizationF2p,
      AnalyticsEvents.checkoutOffer.basisFeatureLockF2p,
    ],
    lockedFeature: ProductFeature.gradeCustomization,
    show: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockGradeCustomizationImp,
      AnalyticsEvents.checkoutOffer.basisFeatureLockImp,
    ],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.BasisFeatureLockAccessRights]: {
    buy: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockAccessRightsF2p,
      AnalyticsEvents.checkoutOffer.basisFeatureLockF2p,
    ],
    lockedFeature: ProductFeature.accessRights,
    show: [
      AnalyticsEvents.checkoutOffer.basisFeatureLockAccessRightsImp,
      AnalyticsEvents.checkoutOffer.basisFeatureLockImp,
    ],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.BasisFeatureLockSupportMeasures]: {
    buy: [AnalyticsEvents.checkoutOffer.basisFeatureLockF2p],
    lockedFeature: ProductFeature.supportMeasures,
    show: [AnalyticsEvents.checkoutOffer.basisFeatureLockImp],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
  [CheckoutOfferSource.BasisFeatureLockUnknown]: {
    buy: [AnalyticsEvents.checkoutOffer.basisFeatureLockUnknownF2p, AnalyticsEvents.checkoutOffer.basisFeatureLockF2p],
    show: [AnalyticsEvents.checkoutOffer.basisFeatureLockUnknownImp, AnalyticsEvents.checkoutOffer.basisFeatureLockImp],
    summary: AnalyticsEvents.funnelSummary.f2p,
  },
};

const LEGACY_CHECKOUT_OFFER_SOURCE_MAP: Record<string, CheckoutOfferSource> = {
  admin_offer: CheckoutOfferSource.TrialAdminPlans,
  discountBtn_offer: CheckoutOfferSource.TrialDiscountButton,
  posttrial_offer: CheckoutOfferSource.BasisIntermittent,
  trial_offer: CheckoutOfferSource.TrialIntermittent,
  upgrade_offer: CheckoutOfferSource.TrialUpgradeButton,
  basis_feature_lock: CheckoutOfferSource.BasisFeatureLockUnknown,
};

function isCheckoutOfferSource(value: unknown): value is CheckoutOfferSource {
  return typeof value === 'string' && Object.values(CheckoutOfferSource).includes(value as CheckoutOfferSource);
}

export function normalizeCheckoutOfferSource(value: unknown): CheckoutOfferSource | null {
  if (isCheckoutOfferSource(value)) return value;
  if (typeof value === 'string') return LEGACY_CHECKOUT_OFFER_SOURCE_MAP[value] ?? null;
  return null;
}

export function getCheckoutOfferShowEvents(
  source: CheckoutOfferSource | null | undefined,
): readonly AnalyticsEventName[] {
  if (!source) return [];
  return CHECKOUT_OFFER_EVENTS[source].show;
}

export function getCheckoutOfferBuyEvents(
  source: CheckoutOfferSource | null | undefined,
): readonly AnalyticsEventName[] {
  if (!source) return [];
  return CHECKOUT_OFFER_EVENTS[source].buy;
}

export function getCheckoutSummaryConversionEvent(
  source: CheckoutOfferSource | null | undefined,
): AnalyticsEventName | null {
  if (!source) return null;
  return CHECKOUT_OFFER_EVENTS[source].summary;
}

export function withCheckoutOfferSourceInReturnPath(
  path: string,
  source: CheckoutOfferSource | null | undefined,
): string {
  try {
    const base = typeof window === 'undefined' ? 'https://app.digidoo.local' : window.location.origin;
    const url = new URL(path || '/', base);
    if (source) {
      url.searchParams.set(CHECKOUT_OFFER_SOURCE_QUERY_PARAM, source);
    } else {
      url.searchParams.delete(CHECKOUT_OFFER_SOURCE_QUERY_PARAM);
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path;
  }
}

export function getCheckoutOfferSourceFromQueryValue(value: unknown): CheckoutOfferSource | null {
  const rawValue = Array.isArray(value) ? value[0] : value;
  return normalizeCheckoutOfferSource(rawValue);
}

type CheckoutTrackingItem = {
  item_brand: 'Digidoo';
  item_category: string;
  item_id: string;
  item_name: string;
  item_variant: string;
  price: number;
  quantity: number;
};

export type CheckoutTrackingPayload = {
  amount_cents?: number;
  billing_interval?: BillingInterval;
  tax?: number;
  currency: string;
  discount_percent?: number;
  express_payment_type?: string;
  items?: CheckoutTrackingItem[];
  locked_feature?: string;
  offer_source?: CheckoutOfferSource;
  owner_type?: string;
  product?: string;
  product_id?: number;
  quantity: number;
  source: string;
  value?: number;
};

export type CheckoutBillingContext = Pick<CheckoutTrackingPayload, 'product' | 'billing_interval'>;

const COUPON_PREFIX_BY_SOURCE: Record<string, string> = {
  onboarding: 'first_year',
  trialUpsell: 'trial_upsell',
  trialWinback: 'winback',
};

function computeCheckoutAmountCents(params: {
  billingInterval: BillingInterval;
  discountPercent?: number;
  plan: ProductWithMeta;
  quantity: number;
}): number {
  const { billingInterval, discountPercent = 0, plan, quantity } = params;
  const normalizedQuantity = Math.max(1, quantity);

  return computeProductPriceBreakdownCents(
    plan,
    {
      team: plan.seatType === SeatType.user ? normalizedQuantity : undefined,
      students: plan.seatType === SeatType.student ? normalizedQuantity : undefined,
    },
    { interval: billingInterval, discountPercent },
  ).totalCents;
}

export function buildCheckoutTrackingPayload(params: {
  /** Revenue excluding tax, in the checkout currency. */
  amountCents?: number;
  taxCents?: number;
  billingInterval: BillingInterval;
  currency?: string;
  offerSource?: CheckoutOfferSource | null;
  plan?: ProductWithMeta;
  quantity?: number;
  source: string;
  discountPercent?: number;
  expressPaymentType?: string;
}): CheckoutTrackingPayload {
  const quantity = Math.max(1, params.quantity ?? 1);
  const offerSource = normalizeCheckoutOfferSource(params.offerSource);
  const payload: CheckoutTrackingPayload = {
    // Analytics currency mirrors the market currency the amounts are denominated in (GA4 convention).
    currency: (params.currency ?? params.plan?.pricing?.currency ?? DEFAULT_CURRENCY).toUpperCase(),
    quantity,
    billing_interval: params.billingInterval,
    source: params.source,
    ...(params.expressPaymentType ? { express_payment_type: params.expressPaymentType } : {}),
  };

  if (offerSource) {
    const offerConfig = CHECKOUT_OFFER_EVENTS[offerSource];
    payload.offer_source = offerSource;
    if (offerConfig.lockedFeature) {
      payload.locked_feature = offerConfig.lockedFeature;
    }
  }

  if (!params.plan) return payload;

  const plan = params.plan;
  const discountPercent =
    params.discountPercent ??
    (params.source === 'trialUpsell' ? getTrialUpsellDiscountPercent(plan, params.billingInterval) : 0);
  const amountCents =
    params.amountCents ??
    computeCheckoutAmountCents({
      billingInterval: params.billingInterval,
      discountPercent,
      plan,
      quantity,
    });

  payload.amount_cents = amountCents;
  if (params.taxCents !== undefined) payload.tax = params.taxCents / 100;
  payload.discount_percent = discountPercent > 0 ? discountPercent : undefined;
  payload.items = [
    {
      item_brand: 'Digidoo',
      item_category: plan.ownerType,
      item_id: String(plan.id),
      item_name: plan.product,
      item_variant: plan.seatType,
      price: amountCents / 100,
      quantity: 1,
    },
  ];
  payload.owner_type = plan.ownerType;
  payload.product = plan.product;
  payload.product_id = plan.id;
  payload.value = amountCents / 100;

  return payload;
}

export function buildPurchaseEventPayload(
  payload: CheckoutTrackingPayload,
  transactionId: string,
): AnalyticsEventPayload {
  const eventPayload: AnalyticsEventPayload = {
    quantity: payload.quantity,
    ...(payload.billing_interval ? { billing_interval: payload.billing_interval } : {}),
    ...(payload.tax !== undefined ? { tax: payload.tax } : {}),
    source: payload.source,
    transaction_id: transactionId,
    ...(payload.amount_cents !== undefined ? { amount_cents: payload.amount_cents } : {}),
    ...(payload.currency ? { currency: payload.currency } : {}),
    ...(payload.discount_percent !== undefined ? { discount_percent: payload.discount_percent } : {}),
    ...(payload.express_payment_type ? { express_payment_type: payload.express_payment_type } : {}),
    ...(payload.items ? { items: payload.items } : {}),
    ...(payload.locked_feature ? { locked_feature: payload.locked_feature } : {}),
    ...(payload.offer_source ? { offer_source: payload.offer_source } : {}),
    ...(payload.owner_type ? { owner_type: payload.owner_type } : {}),
    ...(payload.product ? { product: payload.product } : {}),
    ...(payload.product_id !== undefined ? { product_id: payload.product_id } : {}),
    ...(payload.value !== undefined ? { value: payload.value } : {}),
  };

  if (payload.discount_percent) {
    const couponPrefix = COUPON_PREFIX_BY_SOURCE[payload.source] ?? 'trial_upsell';
    eventPayload.coupon = `${couponPrefix}_${Math.round(payload.discount_percent)}`;
  }

  return eventPayload;
}

export function buildPurchaseUserProperties(params: {
  payload?: CheckoutBillingContext | null;
  userProperties?: AnalyticsUserProperties;
}): AnalyticsUserProperties {
  return {
    billing_state: 'paid',
    ...(params.payload?.product ? { billing_product: params.payload.product } : {}),
    ...(params.payload?.billing_interval ? { billing_interval: params.payload.billing_interval } : {}),
    ...params.userProperties,
  };
}

export function trackCheckoutPurchaseEvents(params: {
  extraPayload?: AnalyticsEventPayload;
  userProperties?: AnalyticsUserProperties;
  payload: CheckoutTrackingPayload;
  trackEvent: Analytics['trackEvent'];
  transactionId: string;
}): AnalyticsEventPayload {
  const purchasePayload = {
    ...buildPurchaseEventPayload(params.payload, params.transactionId),
    ...(params.extraPayload ?? {}),
  };

  const userProperties = buildPurchaseUserProperties(params);
  params.trackEvent(AnalyticsEvents.checkout.purchase, purchasePayload, userProperties);

  const sourceBuyEvents = getCheckoutOfferBuyEvents(params.payload.offer_source);
  for (const sourceBuyEvent of sourceBuyEvents) {
    params.trackEvent(sourceBuyEvent, purchasePayload, userProperties);
  }

  const summaryConversionEvent = getCheckoutSummaryConversionEvent(params.payload.offer_source);
  if (summaryConversionEvent) {
    params.trackEvent(summaryConversionEvent, purchasePayload, userProperties);
  }

  return purchasePayload;
}

export function trackCheckoutSuccessAndPurchaseEvents(params: {
  extraPayload?: AnalyticsEventPayload;
  userProperties?: AnalyticsUserProperties;
  payload: CheckoutTrackingPayload;
  trackEvent: Analytics['trackEvent'];
  transactionId: string;
}): AnalyticsEventPayload {
  params.trackEvent(
    AnalyticsEvents.checkout.success,
    {
      ...params.payload,
      ...(params.extraPayload ?? {}),
    },
    buildPurchaseUserProperties(params),
  );

  return trackCheckoutPurchaseEvents(params);
}

export function createPlanSwitchTransactionId(payload: CheckoutTrackingPayload): string {
  return `plan_switch_${payload.product_id ?? 'unknown'}_${nowMs()}`;
}
